import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, Modal, Space, Table, TableColumnsType, Tag, message, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { regionService } from '../services/regionService';
import type { Region } from '../types/region';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { TextArea, Search } = Input;

const RegionForm: React.FC<{
    isMobile?: boolean,
    onImageChange: (file: File | null) => void,
    selectedFileName: string | null
}> = ({
    isMobile = false,
    onImageChange,
    selectedFileName
}) => (
    <div className={`admin-form-container ${isMobile ? 'mobile-coffee-form' : ''}`}>
        <div className="admin-form-section">
            <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
            <Form.Item
                name="name"
                label="Tên vùng"
                rules={[{ required: true, message: 'Vui lòng nhập tên vùng' }]}
            >
                <Input placeholder="Nhập tên vùng (VD: Cầu Đất, Đà Lạt...)" />
            </Form.Item>

            <Form.Item
                name="country"
                label="Quốc gia"
            >
                <Input placeholder="Nhập tên quốc gia (VD: Việt Nam)" />
            </Form.Item>

            <Form.Item
                name="description"
                label="Mô tả"
                rules={[{ required: true, message: 'Vui lòng nhập mô tả vùng trồng' }]}
            >
                <TextArea
                    rows={4}
                    placeholder="Nhập mô tả về vùng trồng (VD: Vùng trồng cà phê nổi tiếng với độ cao và khí hậu lý tưởng...)"
                />
            </Form.Item>
        </div>

        <div className="admin-form-section">
            <h3 className="admin-form-section-title">Thông tin địa lý</h3>
            <Form.Item label="Độ cao">
                <div className="admin-form-row">
                    <Form.Item
                        name={['altitude', 'min']}
                        label="Tối thiểu"
                        style={{ marginBottom: 0 }}
                    >
                        <InputNumber
                            min={0}
                            addonAfter="m"
                            placeholder="VD: 800"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name={['altitude', 'max']}
                        label="Tối đa"
                        style={{ marginBottom: 0 }}
                    >
                        <InputNumber
                            min={0}
                            addonAfter="m"
                            placeholder="VD: 1600"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </div>
            </Form.Item>

            <Form.Item name="climate" label="Khí hậu">
                <TextArea
                    rows={3}
                    placeholder="Mô tả khí hậu của vùng (VD: Khí hậu ôn hòa, nhiệt độ trung bình 20-25°C...)"
                />
            </Form.Item>

            <Form.Item name="soil" label="Đất đai">
                <TextArea
                    rows={3}
                    placeholder="Mô tả đặc điểm đất đai (VD: Đất bazan màu mỡ, giàu dinh dưỡng...)"
                />
            </Form.Item>
        </div>

        <div className="admin-form-section">
            <h3 className="admin-form-section-title">Hình ảnh</h3>
            <Form.Item label="Hình ảnh">
                <div className="admin-image-upload-section">
                    <Space direction="vertical">
                        <Button onClick={() => document.getElementById('fileInput')?.click()}>
                            Chọn ảnh
                        </Button>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => onImageChange(e.target.files?.[0] || null)}
                            id="fileInput"
                            style={{ display: 'none' }}
                        />
                        {selectedFileName && (
                            <Tag color="blue">
                                File đã chọn: {selectedFileName}
                            </Tag>
                        )}
                    </Space>
                </div>
            </Form.Item>
        </div>
    </div>
);

const MemoizedRegionForm = React.memo(RegionForm);

const RegionManagement: React.FC = () => {
    const [regions, setRegions] = useState<Region[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRegion, setEditingRegion] = useState<Region | null>(null);
    const [form] = Form.useForm();
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth <= 400;
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const titleRef = useRef(null);
    const searchRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableContainerRef = useRef(null);
    const isMountedRef = useRef(true);
    const hasFetchedRegionsRef = useRef(false);
    const fetchRegionsPromiseRef = useRef<Promise<void> | null>(null);

    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý vùng trồng',
            description: 'Chào mừng bạn đến với trang Quản lý vùng trồng! Đây là nơi bạn có thể quản lý danh sách các vùng trồng cà phê, thêm mới, chỉnh sửa và cập nhật thông tin chi tiết về từng vùng.',
            target: () => titleRef.current,
        },
        {
            title: 'Tìm kiếm',
            description: 'Tìm kiếm nhanh các vùng trồng theo tên hoặc quốc gia. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
            target: () => searchRef.current,
        },
        {
            title: 'Thêm vùng trồng mới',
            description: 'Nhấn vào đây để thêm một vùng trồng mới vào hệ thống. Bạn sẽ cần điền thông tin như tên vùng, quốc gia, mô tả, độ cao, khí hậu, đất đai và hình ảnh.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Bảng dữ liệu vùng trồng',
            description: 'Bảng hiển thị tất cả các vùng trồng đã được thêm vào hệ thống. Mỗi hàng chứa thông tin chi tiết về vùng trồng và các nút thao tác để chỉnh sửa hoặc xóa.',
            target: () => tableContainerRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý danh mục vùng trồng cà phê. Bạn có thể thêm mới, chỉnh sửa, xóa các vùng trồng và sử dụng tìm kiếm để tìm kiếm nhanh chóng.',
            target: () => titleRef.current,
        },
    ];

    const fetchRegions = useCallback(async (force = false) => {
        if (!force && hasFetchedRegionsRef.current) {
            return;
        }

        if (fetchRegionsPromiseRef.current) {
            return fetchRegionsPromiseRef.current;
        }

        fetchRegionsPromiseRef.current = (async () => {
            if (isMountedRef.current) {
                setLoading(true);
            }

            try {
                const data = await regionService.getAllRegions();
                if (isMountedRef.current) {
                    setRegions(data);
                    hasFetchedRegionsRef.current = true;
                }
            } catch (error) {
                console.error('Error fetching regions:', error);
                if (isMountedRef.current) {
                    message.error('Không thể tải danh sách vùng trồng');
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
                fetchRegionsPromiseRef.current = null;
            }
        })();

        return fetchRegionsPromiseRef.current;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        fetchRegions();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchRegions]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedSearchText(searchText.trim().toLowerCase());
        }, 250);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    const handleEdit = useCallback((record: Region) => {
        setEditingRegion(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    }, [form]);

    const handleDelete = useCallback(async (id: string) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa vùng trồng này?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    await regionService.deleteRegion(id);
                    setRegions((previousRegions) => previousRegions.filter((region) => region.id !== id));
                    message.success('Xóa vùng trồng thành công');

                    // Gọi api gửi dữ liệu vùng trồng để AI tự động cập nhật
                    // await axios.get(`https://coffee-ai.8am.vn/update`);
                } catch (error) {
                    console.error('Error deleting region:', error);
                    message.error('Không thể xóa vùng trồng');
                }
            }
        });
    }, []);

    const handleSubmit = useCallback(async (values: any) => {
        try {
            setSubmitLoading(true);
            const formattedValues = {
                name: values.name,
                description: values.description,
                country: values.country || '',
                altitude: values.altitude ? {
                    min: values.altitude.min || '',
                    max: values.altitude.max || ''
                } : {},
                climate: values.climate || '',
                soil: values.soil || '',
                createdAt: new Date(),
                updatedAt: new Date()
            };

            if (editingRegion) {
                const updatedRegion = await regionService.updateRegion(editingRegion.id!, formattedValues, imageFile || undefined);
                setRegions((previousRegions) =>
                    previousRegions.map((region) =>
                        region.id === editingRegion.id
                            ? { ...region, ...updatedRegion, id: editingRegion.id }
                            : region
                    )
                );
            } else {
                const addedRegion = await regionService.addRegion(formattedValues, imageFile || undefined);
                setRegions((previousRegions) => [addedRegion as Region, ...previousRegions]);
            }

            setModalVisible(false);
            form.resetFields();
            setImageFile(null);
            message.success(`${editingRegion ? 'Cập nhật' : 'Thêm'} vùng trồng thành công`);

            // Gọi api gửi dữ liệu vùng trồng để AI tự động cập nhật
            // await axios.get(`https://coffee-ai.8am.vn/update`);   
        } catch (error) {
            console.error('Error saving region:', error);
            message.error('Có lỗi xảy ra khi lưu thông tin');
        } finally {
            setSubmitLoading(false);
        }
    }, [editingRegion, form, imageFile]);

    const filteredRegions = useMemo(() => {
        if (!debouncedSearchText) {
            return regions;
        }

        return regions.filter((region) =>
            region.name.toLowerCase().includes(debouncedSearchText) ||
            (region.country?.toLowerCase() || '').includes(debouncedSearchText)
        );
    }, [regions, debouncedSearchText]);

    const columns: TableColumnsType<Region> = useMemo(() => [
        {
            title: 'Hình ảnh',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 100, 
            render: (url: string) => url ? <img src={url} alt="region" style={{ width: 50, height: 50, objectFit: 'cover' }} /> : null
        },
        {
            title: 'Tên vùng',
            dataIndex: 'name',
            key: 'name',
            width: 100, 
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            key: 'description',
            width: 200, 
            },
        {
            title: 'Quốc gia',
            dataIndex: 'country',
            key: 'country',
            width: 100, 
        },
        {
            title: 'Độ cao',
            dataIndex: 'altitude',
            key: 'altitude',
            width: 100, 
            render: (altitude: { min: number; max: number }) =>
                altitude?.min && altitude?.max ? `${altitude.min}m - ${altitude.max}m` : ''
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (_: any, record: Region) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            handleEdit(record);
                        }}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ], [handleDelete, handleEdit]);

    const mobileColumns: TableColumnsType<Region> = useMemo(() => [
        {
            title: 'Thông tin',
            dataIndex: 'name',
            key: 'info',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                        <img src={record.imageUrl} alt="region" style={{ width: 50, height: 50, objectFit: 'cover' }} />
                    </div>

                    <strong style={{ fontSize: '16px' }}>{record.name}</strong>
                    <div style={{ fontSize: '12px' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <div>
                                <strong>Mô tả:</strong>
                                <div style={{
                                    marginTop: 4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: '#666'
                                }}>
                                    {record.description}
                                </div>
                            </div>

                            {/* <div>
                                <strong>Độ cao:</strong>{' '}
                                <Tag>{record.altitude?.min}m - {record.altitude?.max}m</Tag>
                            </div>

                            <div>
                                <strong>Khí hậu:</strong>
                                <div style={{
                                    marginTop: 4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: '#666'
                                }}>
                                    {record.climate}
                                </div>
                            </div>

                            <div>
                                <strong>Đất đai:</strong>
                                <div style={{
                                    marginTop: 4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    color: '#666'
                                }}>
                                    {record.soil}
                                </div>
                            </div> */}
                        </Space>
                    </div>
                </div>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record.id!)}
                    />
                </Space>
            )
        }
    ], [handleDelete, handleEdit]);

    const handleOpenCreateModal = useCallback(() => {
        setEditingRegion(null);
        form.resetFields();
        setImageFile(null);
        setModalVisible(true);
    }, [form]);

    const handleCloseModal = useCallback(() => {
        setModalVisible(false);
        form.resetFields();
        setImageFile(null);
    }, [form]);

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý vùng trồng</h1>
                    <Tag color="blue">{regions.length} vùng trồng</Tag>
                    <Button 
                        type="primary" 
                        icon={<QuestionCircleOutlined />} 
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>
                
                <div className="admin-page-actions">
                    <div className="admin-search-filters">
                        <div ref={searchRef}>
                            <Search
                                placeholder="Tìm theo tên hoặc quốc gia"
                                allowClear
                                style={{ width: isMobile ? '100%' : 300 }}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                    </div>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenCreateModal}
                        className="admin-button"
                        ref={addButtonRef}
                    >
                        Thêm vùng trồng mới
                    </Button>
                </div>
            </div>

            <div className="admin-table-container" ref={tableContainerRef}>
                <Table
                    className="admin-table"
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={filteredRegions}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: isMobile ? undefined : 1500 }}
                    pagination={{
                        responsive: true,
                        defaultPageSize: isMobile ? 5 : 10,
                        position: isMobile ? ['bottomCenter'] : ['bottomRight']
                    }}
                />
            </div>

            <Modal
                title={editingRegion ? "Sửa thông tin vùng trồng" : "Thêm vùng trồng mới"}
                visible={modalVisible}
                onCancel={handleCloseModal}
                footer={null}
                width={800}
                className="admin-modal"
            >
                <div className="admin-form-container">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <MemoizedRegionForm
                            isMobile={isMobile}
                            onImageChange={setImageFile}
                            selectedFileName={imageFile?.name || null}
                        />
                        <Form.Item>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={submitLoading}
                                disabled={submitLoading}
                                className="admin-button"
                            >
                                {editingRegion ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default RegionManagement;